import { AgreementTermTypes } from '@omnea-app/packages/universal/agreement/agreementTermTypes'
import { FormQuestion } from '@omnea-app/packages/universal/formBuilder/form'
import { getStateValues } from '@omnea-app/packages/universal/supplier/stateMapping'
import { AddressFormQuestion } from '../../form/elements/address'
import { AgreementFormQuestion } from '../../form/elements/agreement'
import { CatalogueBasketFormQuestion } from '../../form/elements/catalogueBasket'
import { ContractPeriodFormQuestion } from '../../form/elements/contractPeriod'
import { CountryFormQuestion } from '../../form/elements/country'
import { CountryMultipleFormQuestion } from '../../form/elements/countryMultiple'
import { FilesFormQuestion, REVIEW_OUTCOMES } from '../../form/elements/files'
import { LineItemsFormQuestion } from '../../form/elements/lineItems'
import { PricingFormQuestion } from '../../form/elements/pricing'
import { ProductFormQuestion } from '../../form/elements/product'
import { SupplierCategoriesFormQuestion } from '../../form/elements/supplierCategories'
import { INHERENT_RISK_TIERS } from '../../risks/inherentRiskTier'
import {
  EXISTING_SUPPLIER_VARIABLE,
  EXISTING_AGREEMENT_VARIABLE,
  EXISTING_SUPPLIER_SUBSIDIARY,
  SUPPLIER_PREFERRED_VALUES,
} from '../constants'
import { ConditionOperator } from '../schema'
import { createConditionDiscreteOption } from '../validation/conditionVariableUtils'

export const supportedCustomConditionQuestionTypes = [
  'product',
  'supplierCategories',
  'agreement',
  'contractPeriod',
  'pricing',
  'lineItems',
  'address',
  'country',
  'countryMultiple',
  'files',
  'catalogueBasket',
]

export type CustomConditionsQuestion =
  | (ProductFormQuestion | SupplierCategoriesFormQuestion)
  | AgreementFormQuestion
  | CatalogueBasketFormQuestion
  | ContractPeriodFormQuestion
  | PricingFormQuestion
  | LineItemsFormQuestion
  | AddressFormQuestion
  | CountryFormQuestion
  | CountryMultipleFormQuestion
  | FilesFormQuestion

export const isCustomConditionsQuestion = (question: FormQuestion): question is CustomConditionsQuestion => {
  return supportedCustomConditionQuestionTypes.includes(question.type)
}

export const filesQuestionHasDocumentReviewConfigured = (question: FilesFormQuestion): boolean => {
  return Boolean(question.documentExtractionEnabled && question.documentTypeId)
}

/**
 * Create "Existing Supplier" discrete option
 */
export const createExistingSupplierConditionOption = () => {
  return createConditionDiscreteOption(EXISTING_SUPPLIER_VARIABLE)
}

/**
 * Create "Supplier State" discrete options
 */
export const createSupplierStateConditionOptions = () => {
  return getStateValues().map((value) => createConditionDiscreteOption(value))
}

/**
 * Create "Supplier Preferred" discrete options
 */
export const createSupplierPreferredConditionOptions = () => {
  return SUPPLIER_PREFERRED_VALUES.map((value) => createConditionDiscreteOption(value))
}

export const SUPPLIER_PREFERRED_INVALID_OPERATORS: ConditionOperator[] = [
  'CONTAINS',
  'NOT_CONTAINS',
  'IS_ONE_OF',
  'IS_NOT_ONE_OF',
  'IS_EMPTY',
  'IS_NOT_EMPTY',
]

const PURCHASE_ORDER_STATUS_VALUES = ['PENDING', 'ACTIVE'] as const

/**
 * Create "Purchase Order Status" discrete options
 */
export const createPurchaseOrderStatusConditionOptions = () => {
  return PURCHASE_ORDER_STATUS_VALUES.map((value) => createConditionDiscreteOption(value))
}

/**
 * Create "Existing Agreement" discrete option
 */
export const createExistingAgreementConditionOption = () => {
  return createConditionDiscreteOption(EXISTING_AGREEMENT_VARIABLE)
}

/**
 * Create "Product State" discrete options
 */
export const createProductStateConditionOptions = () => {
  return (['PENDING', 'ACTIVE', 'INACTIVE'] as const).map((value) => createConditionDiscreteOption(value))
}

/**
 * Create "Inherent Risk Tier" discrete options
 */
export const createInherentRiskTierConditionOptions = () => {
  return INHERENT_RISK_TIERS.map((value) => createConditionDiscreteOption(value))
}

/**
 * Tier is a closed enum (Low/Medium/High/Critical), so substring operators don't apply.
 */
export const TIER_INVALID_OPERATORS: ConditionOperator[] = ['CONTAINS', 'NOT_CONTAINS']

/**
 * Create "Existing Supplier Subsidiary" discrete option
 */
export const createExistingSupplierSubsidiaryConditionOption = () => {
  return createConditionDiscreteOption(EXISTING_SUPPLIER_SUBSIDIARY)
}

/**
 * Create contract period term type discrete options
 */
export const createContractPeriodTermTypeConditionOptions = () => {
  return AgreementTermTypes.map((termType) => createConditionDiscreteOption(termType))
}

export const createDocumentReviewOutcomeConditionOptions = () => {
  return REVIEW_OUTCOMES.map((outcome) => createConditionDiscreteOption(outcome))
}
